import React, { useEffect, useMemo } from 'react'
import { useGLTF, useTexture, useVideoTexture } from '@react-three/drei'
import useGame from './stores/useGame.js'
import * as THREE from 'three'

export default function HuoneBaked({ current, ...props }) {
  const { nodes, materials } = useGLTF('/Huone_baked2-draco.glb')

  // ✅ Ensure UV2 exists for lightmaps
  useMemo(() => {
    Object.values(nodes).forEach((node) => {
      if (node.geometry?.attributes?.uv && !node.geometry.attributes.uv2) {
        node.geometry.setAttribute('uv2', node.geometry.attributes.uv)
      }
    })
  }, [nodes])

  // ✅ TV video texture
  const tvTexture = useVideoTexture('/Videos/Hahmo.mp4', {
    loop: true,
    muted: true,
    start: true,
    crossOrigin: 'anonymous',
    playsInline: true,
  })

  // ✅ Textures
  const [aoMap, giMap, beautyMap, ikkunaTexture, beautyMap2,tauluTexture] = useTexture([
    '/Textures/Huone_baked_AO2.webp',
    '/Textures/Huone_baked_GI2.webp',
    '/Textures/Huone_baked_Beauty2.webp',
    '/Images/ikkuna.webp',
    '/Textures/Huone_baked_paa_Beauty2.webp',
    '/Images/taulu.webp'

  ])

  ikkunaTexture.encoding = THREE.sRGBEncoding
  ikkunaTexture.minFilter = THREE.LinearMipmapLinearFilter
  ikkunaTexture.magFilter = THREE.LinearFilter
  ikkunaTexture.anisotropy = 4

  tauluTexture.flipY = true
  tauluTexture.encoding = THREE.sRGBEncoding
  tauluTexture.minFilter = THREE.LinearMipmapLinearFilter
  tauluTexture.magFilter = THREE.LinearFilter
  tauluTexture.anisotropy = 16
//tauluTexture.wrapS = THREE.RepeatWrapping
//tauluTexture.wrapT = THREE.RepeatWrapping
//tauluTexture.repeat.x = -1   // horizontal flip
//tauluTexture.repeat.y = -1   // vertical flip
tauluTexture.needsUpdate = true

  ;[aoMap, giMap, beautyMap, beautyMap2].forEach((tex) => {
    tex.flipY = false
    tex.generateMipmaps = true
    tex.minFilter = THREE.LinearMipmapLinearFilter
    tex.magFilter = THREE.LinearFilter
    tex.anisotropy = 8
  })

  aoMap.encoding = THREE.sRGBEncoding
  giMap.encoding = THREE.sRGBEncoding

  // ✅ Apply baked maps
  useEffect(() => {
    Object.entries(materials).forEach(([name, mat]) => {
      if (['Mat.2', 'Mat.3'].includes(name)) {
        mat.map = beautyMap
        mat.aoMap = aoMap
        mat.aoMapIntensity = 0.5
        mat.lightMap = giMap
        mat.lightMapIntensity = 6
        mat.needsUpdate = true
      }
    })
  }, [materials, aoMap, giMap, beautyMap])

  // ✅ Main baked material
  useEffect(() => {
    Object.entries(materials).forEach(([name, mat]) => {
      if (['Mat.1'].includes(name)) {
        mat.map = beautyMap2
        mat.needsUpdate = true
      }
    })
  }, [beautyMap2])

  
  return (
    <>
      {(current === 'table' || current === 'home' || current === 'wall') && (
        <>
          <directionalLight castShadow position={[1, 2, 3]} intensity={1} />
          <ambientLight intensity={0} />
        </>
      )}

      {/* TAULU image overlay */}
      <mesh
        position={[-4.97, 2.97, -3.35]} // same as taulu mesh
          rotation={[0, Math.PI / 2, 0]}  // rotate 90 degrees on Y-axis

        scale={[1.75, 2.49, 1]}         // match the original taulu size
      >
        <planeGeometry args={[1, 1]} />
        <meshBasicMaterial
          map={tauluTexture}
          toneMapped={false}
          side={THREE.FrontSide}
        />
      </mesh>

      {/* ✅ TV and environment */}
      {current === 'home' && (
        <group>
          <mesh
            name="TV"
            dispose={null}
            position={[-1.85, 0.82, -4.72]}
            rotation={[0, 0, 0]}
            scale={1.45}
          >
            <planeGeometry args={[1.92, 1.08]} />
            <meshBasicMaterial
              map={tvTexture}
              toneMapped={false}
              side={THREE.FrontSide}
            />
          </mesh>

          {/* Background image (window) */}
          <mesh
            position={[-8, 3, 0]}
            rotation={[0, Math.PI * 0.5, 0]}
            scale={[25, 15, 1]}
          >
            <planeGeometry />
            <meshBasicMaterial
              map={ikkunaTexture}
              toneMapped={false}
              side={THREE.FrontSide}
            />
          </mesh>

          {/* Room walls */}
          <mesh
            position={[10.5, 2, 0]}
            rotation={[0, -Math.PI * 0.5, 0]}
            scale={15}
          >
            <planeGeometry />
            <meshBasicMaterial color="#93b5b7" />
          </mesh>
          <mesh
            position={[1.5, 2, 6.5]}
            rotation={[0, -Math.PI, 0]}
            scale={15}
          >
            <planeGeometry />
            <meshBasicMaterial color="#68979a" />
          </mesh>
          <mesh
            position={[0, 8, 0]}
            rotation={[Math.PI / 2, 0, 0]}
            scale={15}
          >
            <planeGeometry />
            <meshBasicMaterial color="#93b5b7" />
          </mesh>
        </group>
      )}

      {/* ✅ NEW GEOMETRY (from updated Draco GLB) */}
      <group {...props} dispose={null} visible={current === 'table' || current === 'home' || current === 'wall'}>
        <mesh
          geometry={nodes.pää.geometry}
          material={materials['Mat.1']}
          position={[-21.36, -3.29, 9.71]}
          scale={1.6}
        />
        <group position={[-9.96, 1.17, -10.97]}>
          <mesh
            geometry={nodes['HUONE-Mat2'].geometry}
            material={materials['Mat.2']}
            position={[19.23, 1.31, 12.11]}
            scale={33.56}
          />
          <mesh
            geometry={nodes['HUONE-lasi'].geometry}
            position={[1.63, -10.16, 16.3]}
            scale={1.38}
          >
            <meshStandardMaterial
              color="#cce7ec"
              transparent
              opacity={0.55}
              roughness={0.08}
              metalness={0.0}
              envMapIntensity={0.8}
              depthWrite={false}
            />
          </mesh>
          <mesh
            geometry={nodes['HUONE-taulu'].geometry}
            material={materials.taulu}
            position={[-9.96, 1.44, -2.47]}
            scale={4.98}
          />
          <mesh
            geometry={nodes['HUONE-Mat3'].geometry}
            material={materials['Mat.3']}
            position={[-11.47, -5.45, 24.77]}
            scale={0.67}
          />
          <mesh
            geometry={nodes['HUONE-Mat2_1'].geometry}
            material={materials['Mat.2']}
            position={[-13.99, 4.53, 18.52]}
            scale={10.6}
          />
          <mesh
            geometry={nodes['HUONE-Mat3_1'].geometry}
            material={materials['Mat.3']}
            position={[-6.99, -0.92, 9.04]}
            scale={2.18}
          />
          <mesh
            geometry={nodes['HUONE-Mat3_2'].geometry}
            material={materials['Mat.3']}
            position={[-4.42, -7.1, 3.82]}
            scale={7.55}
          />
          <mesh
            geometry={nodes['HUONE-Mat2_2'].geometry}
            material={materials['Mat.2']}
            position={[4.61, -9.75, 28.77]}
            scale={4.82}
          />
          <mesh
            geometry={nodes['HUONE-Mat2_3'].geometry}
            material={materials['Mat.2']}
            position={[2.42, -6.93, 17.41]}
            scale={1.62}
          />
          <mesh
            geometry={nodes['HUONE-Mat3_3'].geometry}
            material={materials['Mat.3']}
            position={[1.88, -8.96, 16.39]}
            scale={1.55}
          />
          <mesh
            geometry={nodes['HUONE-Mat2_4'].geometry}
            material={materials['Mat.2']}
            position={[5.79, -14.57, 14.4]}
            scale={15}
          />
          <mesh
            geometry={nodes['HUONE-Mat2_5'].geometry}
            material={materials['Mat.2']}
            position={[-11.6, -4.08, 22.86]}
            scale={1.97}
          />
          <mesh
            geometry={nodes['HUONE-Mat2_6'].geometry}
            material={materials['Mat.2']}
            position={[-0.3, 3.12, -10.96]}
            scale={5.84}
          />
          <group position={[3.17, -10.64, 24.72]}>
            <mesh
              geometry={nodes.Monstera_Deliciosa_02_1.geometry}
              material={nodes.Monstera_Deliciosa_02_1.material}
              position={[-14.61, 6.31, -7.64]}
              scale={1.34}
            />
            <mesh
              geometry={nodes['Monstera_Deliciosa_02-Mat3'].geometry}
              material={materials['Mat.3']}
              position={[15.17, 2.61, 7.69]}
              scale={6.39}
            />
            <mesh
              geometry={nodes['Monstera_Deliciosa_02-Monstera_Leaf_01_Front'].geometry}
              material={materials['Monstera Leaf 01 Front']}
              position={[0.18, 7.24, 1.05]}
              scale={19.26}
            />
          </group>
        </group>
      </group>
    </>
  )
}

useGLTF.preload('/Huone_baked2-draco.glb')
